/**
 * The Dash tab, as a board the rep composes (docs/ACCOUNT-BOARD.md).
 *
 * Replaces the fixed Dash stack: same panels, but the rep places, sizes and
 * removes them, and can pull anything that used to live behind one of the
 * other tabs onto the overview. The tabs stay — they are what a widget's
 * header opens when you want the full version.
 *
 * Arrangement is the shared @techstack/ui AccountBoard against the
 * @techstack/core catalog, so a widget means the same thing here as it does on
 * the GAS account Dash. Only the registry is TekStak's.
 */

import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AccountBoard } from '@techstack/ui';
import { ACCOUNT_BOARDS } from '@techstack/core';
import { ACCOUNT_TABS } from '../AccountsTabs';
import { TEKSTAK_WIDGETS, TEKSTAK_WIDGET_EDITORS } from './widgets';
import { BoardAccountProvider } from './BoardContext';
import useAccountDashboard from '../../../hooks/useAccountDashboard';
import { useRole } from '../../../hooks/useRole';
// react-grid-layout owns the transform positioning and the resize-handle
// geometry; account-board.css only re-skins that chrome, so it comes second.
import 'react-grid-layout/css/styles.css';
import '@techstack/ui/dist/styles/account-board.css';
import './board.css';

const TAB_KEYS = new Set(ACCOUNT_TABS.map((t) => t.key));

export default function AccountsBoard({
  selectedDepartment, departments, onOpenTab, toolbarInto = null, board = 'overview',
}) {
  const navigate = useNavigate();
  const dashboard = useAccountDashboard(board);
  const boardMeta = ACCOUNT_BOARDS[board] || ACCOUNT_BOARDS.overview;
  const { isFeatureEnabled } = useRole();
  const newsEnabled = isFeatureEnabled('news_feed');

  const value = useMemo(() => ({
    selectedDepartment,
    departments,
    deptKey: selectedDepartment?.acronym || selectedDepartment?.official_name || null,
  }), [selectedDepartment, departments]);

  // Role and feature gates run BEFORE the user's arrangement, never after:
  // hiding a widget is a layout choice, offering one they aren't entitled to
  // would be an access-control bug. A gated-off widget already on a saved
  // board keeps its slot and renders its own empty state.
  // A board also offers only its own groups — the People board's add menu is
  // People widgets. Same rule, same seam: it filters the MENU, never a saved
  // board, so a widget already placed keeps its slot whatever group it is in.
  const groups = boardMeta.groups;
  const catalogFilter = useCallback(
    (meta) => (meta.type === 'news' ? newsEnabled : true)
      && (groups === null || groups.indexOf(meta.group) !== -1),
    [newsEnabled, groups],
  );

  // The catalog is shared, so a `deepTab` can name a tab only the other shell
  // has. Say so rather than rendering an arrow that goes nowhere.
  // ...and a widget cannot open the tab it is already sitting on. Now that a
  // tab can BE a board, `org-chart` on the People board points its "open the
  // full view" arrow at People — an arrow back to the page you are reading.
  const canOpenWidget = useCallback(
    (meta) => {
      if (meta.deepTab === boardMeta.tab) return false;
      return meta.deepTab ? TAB_KEYS.has(meta.deepTab) : !!meta.deepHref;
    },
    [boardMeta.tab],
  );

  // A widget already sitting on its own full view says nothing; one whose
  // target is in the other shell still says so.
  const openHintFor = useCallback(
    (meta) => (meta.deepTab === boardMeta.tab ? undefined : 'Full view lives in the other app'),
    [boardMeta.tab],
  );

  const openWidget = useCallback((meta) => {
    if (meta.deepTab === boardMeta.tab) return;
    if (meta.deepTab && TAB_KEYS.has(meta.deepTab)) { onOpenTab?.(meta.deepTab); return; }
    if (meta.deepHref) navigate(meta.deepHref);
  }, [navigate, onOpenTab, boardMeta.tab]);

  return (
    <BoardAccountProvider value={value}>
      <div className="accounts-board">
        <AccountBoard
          host="tekstak"
          config={dashboard.config}
          onChange={dashboard.setConfig}
          onReset={dashboard.reset}
          saveState={dashboard.saveState}
          onRetrySave={dashboard.retry}
          registry={TEKSTAK_WIDGETS}
          configEditors={TEKSTAK_WIDGET_EDITORS}
          catalogFilter={catalogFilter}
          canOpenWidget={canOpenWidget}
          openHintFor={openHintFor}
          onOpenWidget={openWidget}
          toolbarInto={toolbarInto}
        />
      </div>
    </BoardAccountProvider>
  );
}
